<template>
  <section class="detail">
    <button class="back" @click="$router.push('/dashboard')">← Dashboard</button>

    <!-- Error -->
    <div v-if="fileError" class="err">{{ fileError }}</div>

    <!-- Loading -->
    <LoadingSpinnerComponent v-if="isLoading && !file" />

    <div v-else-if="file" class="grid" :class="[{ 'content-loading': isLoading }, (isImage && !thumbFailed) ? 'grid-image' : 'grid-file']">
      <!-- Preview tile: real thumbnail for images, a compact banner otherwise -->
      <div class="lk-card preview">
        <template v-if="isImage && !thumbFailed">
          <div class="preview-photo">
            <img :src="inlineViewUrl" :alt="file.name" class="preview-img" @error="thumbFailed = true" />
          </div>
          <div class="preview-meta">
            <div class="preview-name">{{ file.name }}</div>
            <div class="preview-sub">{{ formatFileSize(file.size_bytes) }} · uploaded {{ formatRelativeTime(file.uploaded) }}</div>
          </div>
        </template>
        <div v-else class="preview-file">
          <div class="ext">{{ ext }}</div>
          <div class="preview-file-meta">
            <div class="preview-name">{{ file.name }}</div>
            <div class="preview-sub">{{ simpleType }} · {{ formatFileSize(file.size_bytes) }} · uploaded {{ formatRelativeTime(file.uploaded) }}</div>
          </div>
        </div>
      </div>

      <!-- Info column -->
      <div class="info">
        <!-- anonymous warning -->
        <div v-if="!file.owner_id" class="anon">
          ⚠ Anonymous file — copy or email this link before you leave; claiming is required to manage it.
        </div>

        <!-- expiry -->
        <div v-if="!isExpired" class="lk-card expiry-card">
          <div class="expiry-label">EXPIRES IN</div>
          <div class="expiry-val mono" :style="{ color: expiringSoon ? 'var(--warn)' : 'var(--accent)' }">{{ remaining }}</div>
        </div>
        <div v-else class="lk-card expired-card">
          <div class="expired-title">This file has expired</div>
          <div class="expired-sub">Downloads are no longer available.</div>
        </div>

        <!-- actions -->
        <div class="lk-card actions-card">
          <button v-if="!isExpired" class="lk-btn lk-btn-primary lk-btn-block download-btn" @click="handleDownloadClick">↓ Download</button>
          <button v-if="!isExpired && (isImage || isVideo)" class="lk-btn lk-btn-block preview-btn" @click="handlePreviewClick">Preview</button>

          <template v-if="!isExpired">
            <div class="share-label">SHARE</div>
            <div class="share-row">
              <span class="mono share-link">{{ shareDisplay }}</span>
              <CopyButton :text="currentUrl" small />
            </div>

            <div class="email-row">
              <input v-model="email" class="lk-input" type="email" placeholder="Send by email…" />
              <button class="lk-btn" :disabled="!isValidEmail(email)" @click="shareLink">✉ Send</button>
            </div>
            <p v-if="emailError" class="email-err">{{ emailError }}</p>
          </template>

          <button v-if="!file.owner_id" class="lk-btn lk-btn-block claim-btn" @click="handleClaimFile">
            {{ isAuthenticated ? '✓ Claim file' : '🔒 Sign in to claim' }}
          </button>
          <button v-if="isFileOwner" class="lk-btn lk-btn-danger lk-btn-block delete-btn" @click="confirmDelete">Delete file</button>
        </div>

        <!-- Short links targeting this file. Signed in: the user's links from
             the backend. Anonymous: creation still works (anon links carry
             claim tokens); the list shows what this session created. -->
        <div class="lk-card links-card">
          <div class="links-label">SHORT LINKS TO THIS FILE</div>

          <div v-if="fileLinks.length" class="link-rows">
            <div v-for="l in fileLinks" :key="l.slug" class="link-row">
              <span class="mono link-slug ellipsis" role="link" tabindex="0"
                    @click="$router.push(`/dashboard/link/${l.slug}`)"
                    @keydown.enter="$router.push(`/dashboard/link/${l.slug}`)">
                {{ shortDisplay(l.slug) }}
              </span>
              <CopyButton :text="shortUrl(l.slug)" small />
            </div>
          </div>
          <div v-else-if="!linksLoading" class="links-empty">
            {{ authedNow ? 'No short links point at this file yet.' : 'Give this file a short link — no account needed.' }}
          </div>

          <div class="link-create-row">
            <div class="link-slug-field">
              <span class="mono slug-host">{{ shortHost }}/</span>
              <input
                v-model="linkSlugInput"
                class="link-slug-input mono"
                placeholder="custom-slug (optional)"
                maxlength="64"
                @keydown.enter="createFileLink"
              />
            </div>
            <button class="lk-btn" :disabled="creatingLink" @click="createFileLink">
              {{ creatingLink ? 'Creating…' : '+ Short link' }}
            </button>
          </div>
          <p v-if="linkError" class="link-err">{{ linkError }}</p>
          <p v-if="!authedNow && fileLinks.length" class="links-anon-note">
            🔒 Created anonymously —
            <button class="link-inline-btn" @click="$router.push({ name: 'Login', query: { redirect: $route.fullPath } })">sign in</button>
            to keep and track these links.
          </p>
        </div>

        <!-- details accordion -->
        <div class="lk-card details">
          <button class="acc-toggle" @click="detailsExpanded = !detailsExpanded">
            <span>Additional details</span>
            <span>{{ detailsExpanded ? '▲' : '▼' }}</span>
          </button>
          <div v-if="detailsExpanded" class="acc-body">
            <div class="d-item"><span class="d-label">Content type</span><span class="mono">{{ file.content_type }}</span></div>
            <div class="d-item"><span class="d-label">File ID</span><span class="mono">{{ file.file_id }}</span></div>
            <div class="d-item"><span class="d-label">Uploaded</span><span>{{ formatTimestamp(file.uploaded) }}</span></div>
            <div class="d-item"><span class="d-label">Expires</span><span>{{ formatTimestamp(file.expires) }}</span></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!isLoading && !fileError" class="empty">
      <div class="empty-icon">📎</div>
      <div class="empty-title">No file details available.</div>
    </div>

    <!-- Delete Modal (Bootstrap) -->
    <div id="deleteModal" class="modal fade" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Deletion</h5>
            <button type="button" class="btn-close" @click="closeConfirmationModal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this file?</p>
            <p class="text-muted small">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="lk-btn" @click="closeConfirmationModal">Cancel</button>
            <button type="button" class="lk-btn lk-btn-solid-danger" @click="deleteFileConfirmed">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal (Bootstrap) -->
    <div v-if="file != null && !isLoading" id="previewModal" class="modal fade" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ file.name }}</h5>
            <button type="button" class="btn-close" @click="closePreviewModal"></button>
          </div>
          <div class="modal-body p-0">
            <div v-if="previewActive && !contentLoaded" class="preview-loading-container">
              <div class="preview-loading-spinner">
                <div class="spinner"></div>
                <p class="mt-2">Loading preview…</p>
              </div>
            </div>
            <div v-if="isImage && previewActive" class="text-center p-2 preview-content" :class="{ loaded: contentLoaded }">
              <img :src="inlineViewUrl" alt="Image preview" class="img-fluid rounded" @load="onContentLoaded"/>
            </div>
            <div v-if="isVideo && previewActive" class="text-center modal-video-wrapper preview-content" :class="{ loaded: contentLoaded }">
              <video controls class="rounded" @loadeddata="onContentLoaded">
                <source :src="inlineViewUrl">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {defineComponent, ref} from 'vue';
import {mapActions, mapGetters} from 'vuex';
import {Modal} from 'bootstrap';
import {config} from '@/runtimeConfig';
import CopyButton from '@/components/ui/CopyButton.vue';
import LoadingSpinnerComponent from '@/components/LoadingSpinnerComponent.vue';
import authReadyLoad from '@/mixins/authReadyLoad';
import {shortUrl, shortDisplay, shortBase, isValidSlug} from '@/utils/links';
import {getSimpleContentType} from '@/utils/fileHelpers';
import {getClaimToken} from '@/utils/claimTokens';
import {
  formatFileSize,
  formatTimestamp,
  formatRelativeTime,
  formatRemainingTime,
  isExpiringSoon,
} from '@/utils';

export default defineComponent({
  name: 'File-Details-View',
  components: {CopyButton, LoadingSpinnerComponent},
  mixins: [authReadyLoad],
  data() {
    return {
      currentUrl: window.location.href,
      modal: null,
      detailsExpanded: false,
      previewActive: false,
      contentLoaded: false,
      thumbFailed: false,
      now: Date.now(),
      ticker: null,
      fileLinks: [],
      linksLoading: false,
      linkSlugInput: '',
      linkError: '',
      creatingLink: false,
      claimAttempted: false,
    };
  },
  created() {
    this.$store.dispatch('files/clearFile');
    try {
      this.$store.dispatch('files/getFile', this.$route.params.id);
    } catch (err) {
      console.log(err);
    }
  },
  mounted() {
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeUnmount() { clearInterval(this.ticker); },
  setup() {
    return {
      email: ref(''),
      emailError: ref(''),
    };
  },
  computed: {
    ...mapGetters('files', ['stateFile', 'stateIsLoading', 'stateFileError']),
    ...mapGetters('auth', ['isAuthenticated']),
    file() { return this.stateFile; },
    isLoading() { return this.stateIsLoading; },
    fileError() { return this.stateFileError; },
    ext() {
      if (!this.file) return '·';
      const e = (String(this.file.name || '').split('.').pop() || '').toUpperCase();
      return e.length > 4 ? e.slice(0, 4) : (e || '·');
    },
    shareDisplay() {
      return `${window.location.host}/files/${this.file ? this.file.file_id : ''}`;
    },
    shortHost() { return shortBase().replace(/^https?:\/\//, ''); },
    // Fresh-at-click-time download endpoints. The metadata response no longer
    // embeds a presigned URL — it would go stale on a long-open page and let
    // downloads bypass the analytics. The backend 302s to a just-minted URL.
    downloadEndpoint() {
      const base = (config.backendBaseUrl || '').replace(/\/+$/, '');
      return this.file ? `${base}/files/${this.file.file_id}/download` : '';
    },
    inlineViewUrl() { return this.downloadEndpoint ? `${this.downloadEndpoint}?inline=true` : ''; },
    isImage() { return this.file && this.file.content_type && this.file.content_type.startsWith('image/'); },
    isVideo() { return this.file && this.file.content_type && this.file.content_type.startsWith('video/'); },
    simpleType() { return this.file && this.file.content_type ? getSimpleContentType(this.file.content_type) : 'File'; },
    isFileOwner() {
      return this.isAuthenticated &&
        this.file &&
        this.file.owner_id &&
        this.$store.state.auth.user &&
        this.file.owner_id === this.$store.state.auth.user.userUUID;
    },
    isExpired() {
      // eslint-disable-next-line no-unused-vars
      const _ = this.now; // reactive tick
      return this.file && this.file.expires ? new Date(this.file.expires).getTime() <= Date.now() : false;
    },
    remaining() {
      // eslint-disable-next-line no-unused-vars
      const _ = this.now;
      return this.file ? formatRemainingTime(this.file.expires) : '';
    },
    expiringSoon() {
      if (!this.file) return false;
      return isExpiringSoon(this.file.expires);
    },
  },
  methods: {
    ...mapActions('files', ['deleteFile', 'shareFileByEmail', 'claimFile']),
    ...mapActions('shortLinks', ['getLinksForFile', 'createLink']),
    formatFileSize,
    formatTimestamp,
    formatRelativeTime,
    formatRemainingTime,
    shortUrl,
    shortDisplay,
    // authReadyLoad mixin: runs once the session restore settles and the user
    // is authenticated (the link list is per-user, so it 401s otherwise).
    async loadAuthedData() {
      this.maybeAutoClaim();
      this.linksLoading = true;
      try {
        this.fileLinks = await this.getLinksForFile(this.$route.params.id) || [];
      } catch (err) {
        console.error('Error loading short links for file:', err);
      } finally {
        this.linksLoading = false;
      }
    },
    // Arriving with ?claim=1 (the post-login return trip from "Sign in to
    // claim") finishes the claim automatically, provided this browser still
    // holds the file's claim token. Runs from both the file watcher and
    // loadAuthedData — whichever settles last has everything it needs.
    async maybeAutoClaim() {
      if (this.$route.query.claim !== '1' || this.claimAttempted) return;
      if (!this.isAuthenticated || !this.file || this.file.owner_id) return;
      if (!getClaimToken('file', this.file.file_id)) return;
      this.claimAttempted = true;
      // Drop the flag from the URL so a refresh doesn't re-claim.
      const query = {...this.$route.query};
      delete query.claim;
      this.$router.replace({query});
      try {
        await this.claimFile(this.file.file_id);
      } catch (error) {
        console.error('Error auto-claiming file:', error);
      }
    },
    async createFileLink() {
      if (this.creatingLink) return;
      this.linkError = '';
      const slug = (this.linkSlugInput || '').trim();
      if (slug && !isValidSlug(slug)) {
        this.linkError = 'Custom link may only contain letters, numbers, - and _';
        return;
      }
      this.creatingLink = true;
      try {
        const link = await this.createLink({
          type: 'file',
          fileId: this.$route.params.id,
          ...(slug ? { slug } : {}),
        });
        this.fileLinks.unshift(link);
        this.linkSlugInput = '';
      } catch (e) {
        // the store already surfaces a toast (e.g. slug taken)
      } finally {
        this.creatingLink = false;
      }
    },
    handleDownloadClick() {
      // The endpoint 302s to a fresh presigned URL with attachment
      // disposition, so the browser downloads rather than navigates.
      const link = document.createElement('a');
      link.href = this.downloadEndpoint;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    confirmDelete() {
      this.modal = new Modal(document.getElementById('deleteModal'));
      this.modal.show();
    },
    closeConfirmationModal() {
      if (this.modal) this.modal.hide();
    },
    handlePreviewClick() {
      this.previewActive = true;
      this.contentLoaded = false;
      this.modal = new Modal(document.getElementById('previewModal'));
      this.modal.show();
    },
    closePreviewModal() {
      if (this.modal) {
        this.modal.hide();
        setTimeout(() => { this.previewActive = false; this.contentLoaded = false; }, 300);
      }
    },
    async deleteFileConfirmed() {
      const file_id = this.file.file_id.toString();
      const result = await this.deleteFile(file_id);
      if (result) setTimeout(() => this.$router.push('/dashboard'), 1500);
      this.closeConfirmationModal();
    },
    isValidEmail(emailAddress) {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      return regex.test(emailAddress);
    },
    shareLink() {
      if (!this.isValidEmail(this.email)) {
        this.emailError = 'Please enter a valid email address';
        return;
      }
      this.emailError = '';
      const file_id = this.file.file_id.toString();
      const email = this.email;
      this.shareFileByEmail({file_id, email})
        .then(() => { this.email = ''; })
        .catch(error => console.error('Error sharing file:', error));
    },
    async handleClaimFile() {
      if (!this.isAuthenticated) {
        // Return here after login; ?claim=1 makes maybeAutoClaim finish the
        // job without another click.
        this.$router.push({name: 'Login', query: {redirect: `${this.$route.path}?claim=1`}});
        return;
      }
      try {
        await this.claimFile(this.file.file_id);
      } catch (error) {
        console.error('Error claiming file:', error);
      }
    },
    onContentLoaded() { this.contentLoaded = true; },
  },
  watch: {
    file(newFile) {
      this.thumbFailed = false;
      document.title = newFile && newFile.name ? `${newFile.name} - Linker.To` : 'File Details - Linker.To';
      if (newFile) this.maybeAutoClaim();
    },
  },
});
</script>

<style scoped>
.detail {
  max-width: 820px;
  margin: 0 auto;
  padding: clamp(20px, 3vw, 36px) clamp(16px, 4vw, 40px) 80px;
}
.back { border: none; background: transparent; color: var(--muted); font-size: 13px; font-weight: 500; cursor: pointer; padding: 0; margin-bottom: 18px; }
.back:hover { color: var(--text); }

.err {
  background: var(--danger-soft);
  border: 1px solid var(--danger);
  color: var(--danger);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;
}

.grid { display: grid; gap: 20px; align-items: start; min-width: 0; }
/* Images get a roomy two-column layout with the photo alongside the actions;
   non-previewable files use a slim full-width banner so the page isn't wide.
   minmax(0, …) lets the tracks shrink below their content's intrinsic width —
   without it, long unbreakable strings (share URL, file id) force the page
   wider than the viewport on narrow screens. */
.grid-image { grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); }
.grid-file { grid-template-columns: minmax(0, 1fr); }
.grid > .info, .grid > .preview { min-width: 0; }
.content-loading { opacity: .6; pointer-events: none; }

.preview { overflow: hidden; }
.preview-photo {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  max-height: 360px;
  overflow: hidden;
  border-bottom: 1px solid var(--border);
}
.preview-img { width: 100%; max-height: 360px; object-fit: contain; display: block; }
.preview-file { display: flex; align-items: center; gap: 14px; padding: 16px 18px; }
.preview-file-meta { min-width: 0; }
.ext {
  width: 46px;
  height: 46px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  font-family: var(--font-mono);
  flex-shrink: 0;
}
.preview-meta { padding: 16px 18px; }
.preview-name { font-size: 15px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.preview-sub { font-size: 12.5px; color: var(--muted); margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.info { display: flex; flex-direction: column; gap: 16px; }
.anon {
  background: var(--warn-soft);
  border: 1px solid var(--warn);
  color: var(--warn);
  border-radius: var(--radius);
  padding: 12px 14px;
  font-size: 12.5px;
  font-weight: 600;
}

.expiry-card { padding: 18px; text-align: center; }
.expiry-label { font-size: 11.5px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 8px; }
.expiry-val { font-family: var(--font-display); font-size: 32px; font-weight: 400; letter-spacing: -.01em; }

.expired-card { padding: 18px; text-align: center; background: var(--danger-soft); border-color: var(--danger); }
.expired-title { font-size: 15px; font-weight: 700; color: var(--danger); }
.expired-sub { font-size: 12.5px; color: var(--muted); margin-top: 4px; }

.actions-card { padding: 18px; }
.download-btn { padding: 12px; margin-bottom: 12px; }
.preview-btn { margin-bottom: 12px; }
.share-label { font-size: 11.5px; font-weight: 600; color: var(--muted); margin-bottom: 7px; }
.share-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 9px 12px;
  margin-bottom: 10px;
}
.share-link { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.email-row { display: flex; gap: 8px; }
.email-row .lk-input { flex: 1; min-width: 0; font-size: 13px; padding: 10px 12px; }
.email-err { color: var(--danger); font-size: 12px; margin: 6px 0 0; }
.claim-btn { margin-top: 12px; }
.delete-btn { margin-top: 10px; }

.links-card { padding: 18px; }
.links-label { font-size: 11.5px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 10px; }
.link-rows { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 8px 12px;
}
.link-slug { flex: 1; font-size: 13px; font-weight: 600; color: var(--accent); cursor: pointer; }
.link-slug:hover { text-decoration: underline; }
.links-empty { font-size: 12.5px; color: var(--faint); margin-bottom: 12px; }
.link-create-row { display: flex; gap: 8px; }
.link-slug-field {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  border-radius: var(--radius);
  padding: 0 10px;
}
.slug-host { color: var(--faint); font-size: 12.5px; }
.link-slug-input { flex: 1; min-width: 0; border: none; background: transparent; color: var(--text); font-size: 13px; padding: 9px 4px; }
.link-err { color: var(--danger); font-size: 12px; margin: 8px 0 0; }
.links-anon-note { color: var(--muted); font-size: 12px; margin: 10px 0 0; }
.link-inline-btn { border: none; background: transparent; color: var(--accent); font-size: 12px; font-weight: 600; cursor: pointer; padding: 0; }
.link-inline-btn:hover { text-decoration: underline; }
.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.details { overflow: hidden; }
.acc-toggle {
  width: 100%;
  border: none;
  background: var(--surface);
  padding: 12px 16px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.acc-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.d-item { display: flex; justify-content: space-between; gap: 12px; font-size: 12.5px; min-width: 0; }
.d-label { color: var(--muted); flex-shrink: 0; }
.d-item > span:last-child { min-width: 0; text-align: right; overflow-wrap: anywhere; }

.mono { font-family: var(--font-mono); }

.empty { text-align: center; padding: 60px 24px; }
.empty-icon { font-size: 32px; margin-bottom: 10px; opacity: .6; }
.empty-title { font-size: 15px; font-weight: 600; color: var(--muted); }

/* Preview modal helpers */
.modal-video-wrapper { position: relative; padding-top: 56.25%; height: 0; overflow: hidden; }
.modal-video-wrapper video { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.preview-loading-container { position: absolute; inset: 0; display: flex; justify-content: center; align-items: center; background: rgba(255,255,255,.9); z-index: 1; }
.preview-loading-spinner { text-align: center; padding: 2rem; }
.preview-loading-spinner .spinner { width: 28px; height: 28px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: lk-spin .8s linear infinite; margin: 0 auto; }
.preview-content { opacity: 0; transition: opacity .3s ease; }
.preview-content.loaded { opacity: 1; }
.img-fluid { max-width: 100%; height: auto; }

@media (max-width: 760px) {
  .grid-image { grid-template-columns: minmax(0, 1fr); }
}
</style>
